import { useToasts, type Toast } from '@/stores/toastStore';
import { Avatar } from '@/art';
import { useT } from '@/i18n/useI18n';

/**
 * Global toast container. Mounted once in App. Renders stacked from the top
 * right; tap to dismiss + run any optional onClick handler.
 */
export function ToastHost() {
  const toasts = useToasts((s) => s.toasts);
  const dismiss = useToasts((s) => s.dismiss);

  if (toasts.length === 0) return null;
  return (
    <div
      style={{
        position: 'fixed',
        // Offset by the device safe-area so toasts never sit under the status
        // bar / notch / rounded corners.
        top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
        right: 'calc(env(safe-area-inset-right, 0px) + 12px)',
        left: 'calc(env(safe-area-inset-left, 0px) + 12px)',
        maxWidth: 360,
        marginLeft: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        zIndex: 500,
        pointerEvents: 'none',
      }}
    >
      {toasts.map((t) => (
        <ToastRow key={t.id} toast={t} onClose={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastRow({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const t = useT();
  const color = toast.kind === 'success' ? 'var(--c-cyan)'
    : toast.kind === 'warn' ? 'var(--c-red)'
    : toast.kind === 'friend' ? 'var(--c-yellow)'
    : 'var(--c-cyan)';
  return (
    <div
      onClick={() => { toast.onClick?.(); onClose(); }}
      style={{
        pointerEvents: 'auto',
        background: 'rgba(15,18,32,0.97)',
        backdropFilter: 'blur(8px)',
        border: `1px solid ${color}40`,
        borderLeft: `4px solid ${color}`,
        borderRadius: 10,
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
        cursor: toast.onClick ? 'pointer' : 'default',
        animation: 'cd-toast-in 0.25s ease-out',
      }}
    >
      {toast.avatarColor && (
        <Avatar color={toast.avatarColor} size={32} photoUrl={toast.avatarUrl ?? undefined} characterUrl={toast.characterUrl} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'var(--f-display)',
            fontSize: 11,
            color,
            letterSpacing: '0.06em',
          }}
        >
          {toast.title}
        </div>
        {toast.body && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-muted)',
              marginTop: 2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {toast.body}
          </div>
        )}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label={t('common.dismiss')}
        style={{
          background: 'transparent',
          border: '1px solid var(--hairline)',
          color: 'var(--ink-muted)',
          width: 22,
          height: 22,
          borderRadius: 6,
          fontSize: 11,
          cursor: 'pointer',
        }}
      >
        ×
      </button>
    </div>
  );
}
